import React, {
  createContext, memo, useCallback, useContext, useMemo, useRef, useState,
} from 'react';
import _ from 'lodash';

import Api from '../../common/services/api';
import { getApiHeaders } from '../../common/services/api/utils';
import { usersSearchAdminHydrationQuery } from '@common/components/predicates-filter/audience-user-search';
import { BASE_URL } from '../../common/constants';
import { useAppSelector } from '@hooks/redux';
import { createCancellableListFetch, isAbortError } from '@common/utils/cancellable-list-fetch';
import {
  countUpcomingAndPastEventIds,
  getRecurringSeriesEventIds,
  RECURRENCE_SCOPES,
  type RecurrenceScope,
} from './utils/recurrence';

import type {
  EventAdminScopeFragment,
  EventForAdminDto,
  EventForUserDto,
  EventGuest,
  EventMutationPayload,
  EventSimpleDto,
  EventStatus,
  EventUserScopeFragment,
  GuestCounts,
} from './types';
import type { ListApiResponse } from '../../common/types/util-types';
import type { LoggedUser, SimpleUser } from '../../common/types/objects';

const adminEventsListFetch = createCancellableListFetch();
const userEventsListFetch = createCancellableListFetch();

export type UserCounts = {
  upcoming: number;
  past: number;
  draft?: number;
  canceled?: number;
};

export type AdminCounts = {
  all: number;
  live: number;
  draft: number;
  canceled: number;
  upcoming?: number;
  past?: number;
};

type AttendanceUser = SimpleUser & {
  first_name: string | null;
  email: string | null;
  last_name: string | null;
  username: string | null;
};

type CommonItems = Record<string, EventSimpleDto>;
type AdminItems = Record<string, EventAdminScopeFragment>;
type UserItems = Record<string, EventUserScopeFragment>;

export interface EventsState {
  items: {
    common: CommonItems;
    admin: AdminItems;
    user: UserItems;
  };
  ids: string[];
  counts: UserCounts;
  userCounts: UserCounts;
  adminCounts: AdminCounts;
  networkCounts: Record<string, AdminCounts>;
  listRevision: number;
  audienceUsers: Record<string, { id: string }>;
}

const emptyUserCounts: UserCounts = {
  upcoming: 0,
  past: 0,
  draft: 0,
  canceled: 0,
};

const emptyAdminCounts: AdminCounts = {
  all: 0,
  live: 0,
  draft: 0,
  canceled: 0,
  upcoming: 0,
  past: 0,
};

const initialEventsState: EventsState = {
  items: {
    common: {},
    admin: {},
    user: {},
  },
  ids: [],
  counts: emptyUserCounts,
  userCounts: emptyUserCounts,
  adminCounts: emptyAdminCounts,
  networkCounts: {},
  listRevision: 0,
  audienceUsers: {},
};

const eventAdminScopeKeys = ['audience', 'going_users'] as const;
const eventUserScopeKeys = [
  'going_users',
  'invited_users_count',
  'remaining_spots_count',
  'is_going',
  'decline_reason',
  'registration_deadline',
] as const;

function withoutKeys<T extends object>(item: T, keys: readonly string[]): T {
  const next = { ...item };
  keys.forEach((key) => {
    delete (next as Record<string, unknown>)[key];
  });
  return next;
}

function withKeys<T extends object>(item: T, keys: readonly string[]) {
  const source = item as Record<string, unknown>;
  return keys.reduce<Record<string, unknown>>((acc, key) => {
    if (key in source) acc[key] = source[key];
    return acc;
  }, {});
}

function storeMany<T extends { id: string }, R>(
  current: Record<string, R>,
  items: T[],
  toStored: (item: T) => R,
) {
  return items.reduce((acc, item) => {
    acc[item.id] = toStored(item);
    return acc;
  }, { ...current });
}

function toCommon(item: EventForAdminDto | EventForUserDto): EventSimpleDto {
  return withoutKeys(item, eventAdminScopeKeys);
}

function toAdminScope(item: EventForAdminDto) {
  return withKeys(item, eventAdminScopeKeys) as EventAdminScopeFragment;
}

function toUserScope(item: EventForAdminDto | EventForUserDto): EventUserScopeFragment {
  const registrationDeadline = 'registration_deadline' in item
    ? item.registration_deadline
    : undefined;
  return {
    ...withKeys(item, eventUserScopeKeys),
    registration_deadline: registrationDeadline ?? item.settings?.registration_deadline ?? null,
  } as EventUserScopeFragment;
}

function toUserCommon(item: EventForUserDto): EventSimpleDto {
  return withoutKeys(item, eventUserScopeKeys);
}

function nextListIds(current: string[], items: Array<{ id: string }>, strategy: 'append' | null) {
  const incoming = items.map((item) => item.id);
  return strategy === 'append' ? [...current, ...incoming] : incoming;
}

function mergeAdminItem(admin: AdminItems, item: EventForAdminDto): AdminItems {
  const existing = admin[item.id] || {};
  const incoming = toAdminScope(item);
  const merged = { ...existing } as Record<string, unknown>;
  eventAdminScopeKeys.forEach((key) => {
    const value = incoming[key];
    if (value !== undefined && value !== null) merged[key] = value;
  });
  return { ...admin, [item.id]: merged as EventAdminScopeFragment };
}

function omitEventIds<T>(items: Record<string, T>, ids: string[]) {
  if (!ids.length) return items;
  const next = { ...items };
  ids.forEach((id) => {
    delete next[id];
  });
  return next;
}

function applyAdminEvents(
  state: EventsState,
  payload: {
    items: EventForAdminDto[];
    counts: AdminCounts;
    strategy: 'append' | null;
    networkId?: string | null;
    storeNetworkId?: string | null;
    countsOnly?: boolean;
  },
): EventsState {
  const {
    items, counts, strategy, networkId, storeNetworkId, countsOnly,
  } = payload;
  const networkKey = storeNetworkId ?? networkId;
  const next: EventsState = {
    ...state,
    adminCounts: storeNetworkId == null && networkId == null
      ? { ...state.adminCounts, ...counts }
      : state.adminCounts,
    counts: {
      ...state.counts,
      draft: counts.draft ?? state.counts.draft,
      canceled: counts.canceled ?? state.counts.canceled,
    },
    networkCounts: networkKey == null
      ? state.networkCounts
      : {
        ...state.networkCounts,
        [String(networkKey)]: { ...emptyAdminCounts, ...counts },
      },
  };

  if (countsOnly) return next;

  return {
    ...next,
    items: {
      ...state.items,
      common: storeMany(state.items.common, items, toCommon),
      admin: storeMany(state.items.admin, items, toAdminScope),
    },
    ids: nextListIds(state.ids, items, strategy),
  };
}

function applyUserEvents(
  state: EventsState,
  payload: {
    items: EventForUserDto[];
    counts: UserCounts;
    strategy: 'append' | null;
    countsOnly?: boolean;
  },
): EventsState {
  const { items, counts, strategy, countsOnly } = payload;
  const next: EventsState = {
    ...state,
    userCounts: { ...state.userCounts, ...counts },
    counts: { ...state.counts, ...counts },
  };

  if (countsOnly) return next;

  return {
    ...next,
    items: {
      ...state.items,
      common: storeMany(state.items.common, items, toUserCommon),
      user: storeMany(state.items.user, items, toUserScope),
    },
    ids: nextListIds(state.ids, items, strategy),
  };
}

function applyReceivedEvent(
  state: EventsState,
  item: EventForAdminDto | EventForUserDto,
): EventsState {
  return {
    ...state,
    items: {
      common: storeMany(state.items.common, [item], toCommon),
      admin: mergeAdminItem(state.items.admin, item as EventForAdminDto),
      user: storeMany(state.items.user, [item], toUserScope),
    },
    ids: state.ids.includes(item.id) ? state.ids : [...state.ids, item.id],
  };
}

function applyCreatedEvent(state: EventsState, item: EventForAdminDto): EventsState {
  return {
    ...state,
    items: {
      ...state.items,
      common: storeMany(state.items.common, [item], toCommon),
      admin: mergeAdminItem(state.items.admin, item),
    },
    ids: state.ids.includes(item.id) ? state.ids : [...state.ids, item.id],
  };
}

function applyUpdatedEvent(state: EventsState, item: EventForAdminDto): EventsState {
  return {
    ...state,
    items: {
      ...state.items,
      common: storeMany(state.items.common, [item], toCommon),
      admin: mergeAdminItem(state.items.admin, item),
    },
  };
}

function applyAttendance(state: EventsState, id: string, user: AttendanceUser): EventsState {
  const adminEvent = state.items.admin[id];
  const userEvent = state.items.user[id];
  return {
    ...state,
    items: {
      ...state.items,
      admin: adminEvent ? {
        ...state.items.admin,
        [id]: {
          ...adminEvent,
          going_users: {
            count: (adminEvent.going_users?.count || 0) + 1,
            users_preview: [...(adminEvent.going_users?.users_preview || []), user],
          },
        },
      } : state.items.admin,
      user: userEvent ? {
        ...state.items.user,
        [id]: {
          ...userEvent,
          is_going: true,
          decline_reason: null,
          going_users: {
            count: (userEvent.going_users?.count || 0) + 1,
            users_preview: [...(userEvent.going_users?.users_preview || []), user],
          },
        },
      } : state.items.user,
    },
  };
}

function applyAbsence(
  state: EventsState,
  id: string,
  userId: string,
  declineReason?: string | null,
): EventsState {
  const adminEvent = state.items.admin[id];
  const userEvent = state.items.user[id];
  return {
    ...state,
    items: {
      ...state.items,
      admin: adminEvent ? {
        ...state.items.admin,
        [id]: {
          ...adminEvent,
          going_users: {
            count: (adminEvent.going_users?.count || 1) - 1,
            users_preview: (adminEvent.going_users?.users_preview || []).filter((guest) => guest.id !== userId),
          },
        },
      } : state.items.admin,
      user: userEvent ? {
        ...state.items.user,
        [id]: {
          ...userEvent,
          is_going: false,
          decline_reason: declineReason ?? null,
          going_users: {
            count: (userEvent.going_users?.count || 1) - 1,
            users_preview: (userEvent.going_users?.users_preview || []).filter((guest) => guest.id !== userId),
          },
        },
      } : state.items.user,
    },
  };
}

function removedEventIds(eventId: string, eventIds: string[]) {
  return eventIds.length ? eventIds : [eventId];
}

function applyCancelledEvents(
  state: EventsState,
  payload: {
    eventId: string;
    eventIds: string[];
    upcomingCount: number;
    pastCount: number;
  },
): EventsState {
  const ids = removedEventIds(payload.eventId, payload.eventIds);
  const removed = ids.length;
  const upcomingRemoved = payload.upcomingCount ?? removed;
  const pastRemoved = payload.pastCount ?? 0;
  return {
    ...state,
    items: {
      common: omitEventIds(state.items.common, ids),
      admin: omitEventIds(state.items.admin, ids),
      user: omitEventIds(state.items.user, ids),
    },
    ids: state.ids.filter((id) => !ids.includes(id)),
    adminCounts: {
      ...state.adminCounts,
      canceled: state.adminCounts.canceled + removed,
      all: Math.max(0, state.adminCounts.all - removed),
    },
    counts: {
      ...state.counts,
      canceled: (state.counts.canceled || 0) + upcomingRemoved + pastRemoved,
      upcoming: Math.max(0, state.counts.upcoming - upcomingRemoved),
      past: Math.max(0, state.counts.past - pastRemoved),
    },
  };
}

function applyDeletedEvents(
  state: EventsState,
  payload: {
    eventId: string;
    eventIds: string[];
    upcomingCount: number;
    pastCount: number;
  },
): EventsState {
  const ids = removedEventIds(payload.eventId, payload.eventIds);
  const removed = ids.length;
  const upcomingRemoved = payload.upcomingCount ?? removed;
  const pastRemoved = payload.pastCount ?? 0;
  return {
    ...state,
    items: {
      common: omitEventIds(state.items.common, ids),
      admin: omitEventIds(state.items.admin, ids),
      user: omitEventIds(state.items.user, ids),
    },
    ids: state.ids.filter((id) => !ids.includes(id)),
    adminCounts: {
      ...state.adminCounts,
      all: Math.max(0, state.adminCounts.all - removed),
    },
    counts: {
      ...state.counts,
      upcoming: Math.max(0, state.counts.upcoming - upcomingRemoved),
      past: Math.max(0, state.counts.past - pastRemoved),
    },
  };
}

function applyDuplicatedEvent(
  state: EventsState,
  response: { data: EventForAdminDto },
  originalEventId: string,
): EventsState {
  const item = response.data;
  return {
    ...state,
    items: {
      common: storeMany(state.items.common, [item], toCommon),
      admin: storeMany(state.items.admin, [item], toAdminScope),
      user: storeMany(state.items.user, [item], toUserScope),
    },
    ids: state.ids.flatMap((id) => (id === originalEventId ? [item.id, id] : [id])),
  };
}

function applyClearedNetworkCounts(state: EventsState, networkId: string): EventsState {
  const networkCounts = { ...state.networkCounts };
  delete networkCounts[networkId];
  return { ...state, networkCounts };
}

function applySeriesMutation(state: EventsState): EventsState {
  return { ...state, listRevision: state.listRevision + 1 };
}

function rememberAudienceUsers(state: EventsState, users: Array<{ id: string }>): EventsState {
  return {
    ...state,
    audienceUsers: users.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, { ...state.audienceUsers }),
  };
}

function selectAdminEvents(state: EventsState): EventForAdminDto[] {
  return state.ids.map((id) => state.items.admin[id] && ({
    ...state.items.common[id],
    ...state.items.admin[id],
  })) as EventForAdminDto[];
}

function selectAdminEvent(state: EventsState, id: string): EventForAdminDto | undefined {
  const admin = state.items.admin[id];
  if (!admin) return undefined;
  return { ...state.items.common[id], ...admin } as EventForAdminDto;
}

function selectUserEvents(state: EventsState): EventForUserDto[] {
  return state.ids.map((id) => state.items.user[id] && ({
    ...state.items.common[id],
    ...state.items.user[id],
  })) as EventForUserDto[];
}

function selectUserEvent(state: EventsState, id: string): EventForUserDto | undefined {
  const user = state.items.user[id];
  if (!user) return undefined;
  return { ...state.items.common[id], ...user } as EventForUserDto;
}

function selectNetworkCounts(state: EventsState, networkId: string | null | undefined) {
  if (!networkId) return emptyAdminCounts;
  return state.networkCounts[String(networkId)] ?? emptyAdminCounts;
}

type AdminEventsFilter = {
  filter?: EventStatus;
  network_ids?: string[];
  types?: string[];
  countsOnly?: boolean;
};

type FetchEventsApiResponse = ListApiResponse<EventForAdminDto[], AdminCounts>;
type FetchMyEventsApiResponse = ListApiResponse<EventForUserDto[], UserCounts>;
type GuestListResponse = ListApiResponse<EventGuest[], GuestCounts>;

export interface EventsContextValue {
  state: EventsState;
  userEvents: EventForUserDto[];
  adminEvents: EventForAdminDto[];
  counts: UserCounts;
  userCounts: UserCounts;
  adminCounts: AdminCounts;
  listRevision: number;
  audienceUsers: EventsState['audienceUsers'];
  networkCounts: (networkId: string | null | undefined) => AdminCounts;
  getAdminScopeEvent: (id: string) => EventForAdminDto | undefined;
  getUserScopeEvent: (id: string) => EventForUserDto | undefined;
  invalidateAdminEventsListFetch: () => void;
  invalidateUserEventsListFetch: () => void;
  fetchAdminEvents: (
    nextCursor: string | null,
    filter: AdminEventsFilter,
    limit?: number,
    storeCountsUnderNetworkId?: string | null,
  ) => Promise<FetchEventsApiResponse | null>;
  createAdminEventsListFetcher: (storeNetworkId: string | null) => (
    nextCursor: string | null,
    filter: AdminEventsFilter,
    limit?: number,
  ) => Promise<FetchEventsApiResponse | null>;
  fetchMyEvents: (
    nextCursor: string | null,
    filter: { filter?: string; types?: string[]; countsOnly?: boolean },
    limit?: number,
  ) => Promise<FetchMyEventsApiResponse | null>;
  fetchEvent: (id: string) => Promise<void>;
  fetchMyEvent: (id: string) => Promise<void>;
  createEvent: (payload: EventMutationPayload) => Promise<{ item: EventForAdminDto }>;
  updateEvent: (id: string, payload: EventMutationPayload) => Promise<{ item: EventForAdminDto }>;
  attendEvent: (id: string) => Promise<void>;
  absentEvent: (id: string, declineReason?: string | null) => Promise<void>;
  cancelEvent: (id: string, scope?: RecurrenceScope) => Promise<void>;
  deleteEvent: (id: string, scope?: RecurrenceScope) => Promise<void>;
  deleteEventType: (name: string) => Promise<void>;
  duplicateEvent: (response: { data: EventForAdminDto }, originalEventId: string) => void;
  clearNetworkCounts: (networkId: string) => void;
  rememberUserEventsList: (
    items: EventForUserDto[],
    counts: Partial<UserCounts> | undefined,
    strategy: 'append' | null,
  ) => void;
  rememberAdminEventsList: (
    items: EventForAdminDto[],
    counts: Partial<AdminCounts> | undefined,
    strategy: 'append' | null,
    networkId: string | null,
    storeNetworkId: string | null,
  ) => void;
  fetchGuestsListCSV: (id: string) => Promise<Blob>;
  fetchGuests: (
    eventId: string,
    nextCursor: string | undefined,
    filters: Partial<{ filter: string; searchTerm: string }>,
    limit?: number,
  ) => Promise<GuestListResponse>;
}

const EventsContext = createContext<EventsContextValue | null>(null);

function isSeriesScope(scope?: RecurrenceScope) {
  return scope === RECURRENCE_SCOPES.all || scope === RECURRENCE_SCOPES.thisAndFollowing;
}

function EventsProviderComponent({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState(initialEventsState);
  const organisationId = useAppSelector((store) => store.organisation.selected?.id);
  const loggedUser = useAppSelector((store) => store.loggedUser.user);

  const stateRef = useRef(state);
  stateRef.current = state;
  const organisationIdRef = useRef(organisationId);
  organisationIdRef.current = organisationId;
  const loggedUserRef = useRef<LoggedUser | undefined>(loggedUser);
  loggedUserRef.current = loggedUser;

  const requireOrganisationId = useCallback(() => {
    const id = organisationIdRef.current;
    if (!id) throw new Error('Organisation is not selected');
    return id;
  }, []);

  const invalidateAdminEventsListFetch = useCallback(() => {
    adminEventsListFetch.invalidate();
  }, []);

  const invalidateUserEventsListFetch = useCallback(() => {
    userEventsListFetch.invalidate();
  }, []);

  const refetchEventLists = useCallback(() => {
    invalidateAdminEventsListFetch();
    invalidateUserEventsListFetch();
    setState(applySeriesMutation);
  }, [invalidateAdminEventsListFetch, invalidateUserEventsListFetch]);

  const fetchAdminEvents = useCallback(async (
    nextCursor: string | null,
    filter: AdminEventsFilter,
    limit = 16,
    storeCountsUnderNetworkId?: string | null,
  ) => {
    const selectedId = requireOrganisationId();
    const { countsOnly, ...filterParams } = filter;
    const isFirstPage = !nextCursor;
    const { signal, generation } = adminEventsListFetch.begin(isFirstPage);
    const query = Api.utils.toQuery({
      limit,
      cursor: nextCursor,
      filter: filterParams.filter,
      network_ids: filterParams.network_ids,
      types: filterParams.types,
    });

    try {
      const request = (await Api.get(
        `/v2/organisations/${selectedId}/events?${query}`,
        true,
        isFirstPage ? { signal } : undefined,
      )) as FetchEventsApiResponse;

      if (adminEventsListFetch.isStale(generation)) return null;

      const networkIds = filterParams.network_ids;
      const storeNetworkId = typeof storeCountsUnderNetworkId === 'string' ? storeCountsUnderNetworkId : null;
      const networkId = networkIds?.[0] ?? null;

      setState((current) => applyAdminEvents(current, {
        items: request.data,
        counts: request.meta.counts,
        strategy: nextCursor ? 'append' : null,
        networkId,
        storeNetworkId,
        countsOnly: countsOnly || false,
      }));

      return request;
    } catch (error) {
      if (isAbortError(error)) return null;
      throw error;
    }
  }, [requireOrganisationId]);

  const fetchAdminEventsRef = useRef(fetchAdminEvents);
  fetchAdminEventsRef.current = fetchAdminEvents;

  const createAdminEventsListFetcher = useCallback((storeNetworkId: string | null) => (
    nextCursor: string | null,
    filter: AdminEventsFilter,
    limit = 16,
  ) => fetchAdminEventsRef.current(nextCursor, filter, limit, storeNetworkId), []);

  const fetchMyEvents = useCallback(async (
    nextCursor: string | null,
    filter: { filter?: string; types?: string[]; countsOnly?: boolean },
    limit = 16,
  ) => {
    const selectedId = requireOrganisationId();
    const { countsOnly, ...filterParams } = filter;
    const isFirstPage = !nextCursor;
    const { signal, generation } = userEventsListFetch.begin(isFirstPage);
    const query = Api.utils.toQuery({
      limit,
      filter: filterParams.filter,
      cursor: nextCursor || true,
      types: filterParams.types,
    });

    try {
      const request = await Api.get<FetchMyEventsApiResponse>(
        `/v1/organisations/${selectedId}/users/me/events?${query}`,
        true,
        isFirstPage ? { signal } : undefined,
      );

      if (userEventsListFetch.isStale(generation)) return null;

      setState((current) => applyUserEvents(current, {
        items: request.data,
        counts: request.meta.counts,
        strategy: nextCursor ? 'append' : null,
        countsOnly: countsOnly || false,
      }));

      return request;
    } catch (error) {
      if (isAbortError(error)) return null;
      throw error;
    }
  }, [requireOrganisationId]);

  const fetchEvent = useCallback(async (id: string) => {
    const selectedId = requireOrganisationId();
    type ApiResponse = { data: EventForAdminDto };
    const { data: item } = await Api.get<ApiResponse>(`/v1/organisations/${selectedId}/events/${id}`);
    const normalizedAudience = Array.isArray(item?.audience) ? item?.audience[0] : item?.audience;
    const predicates = normalizedAudience?.predicates;

    if (Array.isArray(predicates)) {
      const userPredicate = predicates.find((predicate: { attribute?: string; value?: string[] }) => (
        predicate?.attribute === 'users.id'
      ));
      if (userPredicate && Array.isArray(userPredicate.value) && userPredicate.value.length > 0) {
        const userIds = userPredicate.value;
        try {
          const query = Api.utils.toQuery({
            limit: userIds.length,
            ...usersSearchAdminHydrationQuery('event'),
          });
          const usersResponse = await Api.post(`/v1/organisations/${selectedId}/users/search?${query}`, {
            predicate_type: 'match_any',
            predicates: [{ attribute: 'users.id', comparison: 'in', value: userIds }],
          }) as { data?: unknown[]; meta?: { pagination?: { total_count: number } } };

          if (usersResponse?.data) {
            setState((current) => rememberAudienceUsers(
              current,
              usersResponse.data as Array<{ id: string }>,
            ));
          }
        } catch (error) {
          console.error('Failed to fetch missing audience users', error);
        }
      }
    }

    setState((current) => applyReceivedEvent(current, item));
  }, [requireOrganisationId]);

  const fetchMyEvent = useCallback(async (id: string) => {
    const selectedId = requireOrganisationId();
    type ApiResponse = { data: EventForUserDto };
    const { data: item } = await Api.get<ApiResponse>(
      `/v1/organisations/${selectedId}/users/me/events/${id}`,
    );
    setState((current) => applyReceivedEvent(current, item));
  }, [requireOrganisationId]);

  const createEvent = useCallback(async (payload: EventMutationPayload) => {
    const selectedId = requireOrganisationId();
    const { data: item } = await Api.post(
      `/v1/organisations/${selectedId}/events`,
      payload,
    ) as { data: EventForAdminDto };
    setState((current) => applyCreatedEvent(current, item));
    return { item };
  }, [requireOrganisationId]);

  const updateEvent = useCallback(async (id: string, payload: EventMutationPayload) => {
    const selectedId = requireOrganisationId();
    const { data: item } = await Api.put(
      `/v1/organisations/${selectedId}/events/${id}`,
      payload,
    ) as { data: EventForAdminDto };
    setState((current) => applyUpdatedEvent(current, item));
    return { item };
  }, [requireOrganisationId]);

  const attendEvent = useCallback(async (id: string) => {
    const selectedId = requireOrganisationId();
    const user = loggedUserRef.current;
    if (!user) throw new Error('User is not logged in');

    await Api.post(`/v1/organisations/${selectedId}/events/${id}/attendance`, {
      is_going: true,
    });

    setState((current) => applyAttendance(
      current,
      id,
      _.pick(user, [
        'id', 'first_name', 'last_name', 'full_name', 'email', 'username',
        'profile_img', 'last_active',
      ]) as AttendanceUser,
    ));
  }, [requireOrganisationId]);

  const absentEvent = useCallback(async (id: string, declineReason?: string | null) => {
    const selectedId = requireOrganisationId();
    const user = loggedUserRef.current;
    if (!user) throw new Error('User is not logged in');

    await Api.post(`/v1/organisations/${selectedId}/events/${id}/attendance`, {
      is_going: false,
      ...(declineReason != null ? { decline_reason: declineReason } : {}),
    });

    setState((current) => applyAbsence(current, id, user.id, declineReason ?? null));
  }, [requireOrganisationId]);

  const cancelEvent = useCallback(async (id: string, scope?: RecurrenceScope) => {
    const selectedId = requireOrganisationId();
    const { common } = stateRef.current.items;
    const event = common[id];
    const eventIds = getRecurringSeriesEventIds(common, event || { id }, scope);
    const { upcomingCount, pastCount } = scope === RECURRENCE_SCOPES.all
      ? countUpcomingAndPastEventIds(common, eventIds)
      : { upcomingCount: eventIds.length, pastCount: 0 };

    await Api.post(
      `/v1/organisations/${selectedId}/events/${id}/cancel`,
      scope ? { scope } : undefined,
    );

    if (isSeriesScope(scope)) refetchEventLists();

    setState((current) => applyCancelledEvents(current, {
      eventId: id,
      eventIds,
      upcomingCount,
      pastCount,
    }));
  }, [refetchEventLists, requireOrganisationId]);

  const deleteEvent = useCallback(async (id: string, scope?: RecurrenceScope) => {
    const selectedId = requireOrganisationId();
    const { common } = stateRef.current.items;
    const event = common[id];
    const eventIds = getRecurringSeriesEventIds(common, event || { id }, scope);
    const { upcomingCount, pastCount } = scope === RECURRENCE_SCOPES.all
      ? countUpcomingAndPastEventIds(common, eventIds)
      : { upcomingCount: eventIds.length, pastCount: 0 };
    const query = scope ? `?${Api.utils.toQuery({ scope })}` : '';

    await Api.delete(`/v1/organisations/${selectedId}/events/${id}${query}`);

    if (isSeriesScope(scope)) refetchEventLists();

    setState((current) => applyDeletedEvents(current, {
      eventId: id,
      eventIds,
      upcomingCount,
      pastCount,
    }));
  }, [refetchEventLists, requireOrganisationId]);

  const deleteEventType = useCallback(async (name: string) => {
    const selectedId = requireOrganisationId();
    await Api.delete(`/v1/organisations/${selectedId}/events/types/${encodeURIComponent(name)}`);
  }, [requireOrganisationId]);

  const duplicateEvent = useCallback((
    response: { data: EventForAdminDto },
    originalEventId: string,
  ) => {
    setState((current) => applyDuplicatedEvent(current, response, originalEventId));
  }, []);

  const clearNetworkCounts = useCallback((networkId: string) => {
    setState((current) => applyClearedNetworkCounts(current, networkId));
  }, []);

  const rememberUserEventsList = useCallback((
    items: EventForUserDto[],
    counts: Partial<UserCounts> | undefined,
    strategy: 'append' | null,
  ) => {
    setState((current) => applyUserEvents(current, {
      items,
      counts: { ...current.userCounts, ...counts },
      strategy,
    }));
  }, []);

  const rememberAdminEventsList = useCallback((
    items: EventForAdminDto[],
    counts: Partial<AdminCounts> | undefined,
    strategy: 'append' | null,
    networkId: string | null,
    storeNetworkId: string | null,
  ) => {
    setState((current) => applyAdminEvents(current, {
      items,
      counts: { ...(counts ?? {}) } as AdminCounts,
      strategy,
      networkId,
      storeNetworkId,
    }));
  }, []);

  const fetchGuestsListCSV = useCallback(async (id: string) => {
    const selectedId = requireOrganisationId();
    const options: RequestInit = {
      headers: await getApiHeaders(),
      credentials: 'include',
      cache: 'no-cache',
    };
    const response = await fetch(
      `${BASE_URL}/v1/organisations/${selectedId}/events/${id}/guests/export`,
      options,
    );
    return response.blob();
  }, [requireOrganisationId]);

  const fetchGuests = useCallback((
    eventId: string,
    nextCursor: string | undefined,
    filters: Partial<{ filter: string; searchTerm: string }>,
    limit = 10,
  ) => {
    const selectedId = requireOrganisationId();
    const query = Api.utils.toQuery({
      cursor: nextCursor || true,
      limit,
      filter: filters.filter,
      q: filters.searchTerm,
    });
    return Api.get<GuestListResponse>(
      `/v1/organisations/${selectedId}/events/${eventId}/guests?${query}`,
    );
  }, [requireOrganisationId]);

  const getAdminScopeEvent = useCallback(
    (id: string) => selectAdminEvent(stateRef.current, id),
    [],
  );
  const getUserScopeEvent = useCallback(
    (id: string) => selectUserEvent(stateRef.current, id),
    [],
  );
  const networkCounts = useCallback(
    (networkId: string | null | undefined) => selectNetworkCounts(stateRef.current, networkId),
    [],
  );

  const value = useMemo<EventsContextValue>(() => ({
    state,
    userEvents: selectUserEvents(state),
    adminEvents: selectAdminEvents(state),
    counts: state.counts,
    userCounts: state.userCounts,
    adminCounts: state.adminCounts,
    listRevision: state.listRevision,
    audienceUsers: state.audienceUsers,
    networkCounts,
    getAdminScopeEvent,
    getUserScopeEvent,
    invalidateAdminEventsListFetch,
    invalidateUserEventsListFetch,
    fetchAdminEvents,
    createAdminEventsListFetcher,
    fetchMyEvents,
    fetchEvent,
    fetchMyEvent,
    createEvent,
    updateEvent,
    attendEvent,
    absentEvent,
    cancelEvent,
    deleteEvent,
    deleteEventType,
    duplicateEvent,
    clearNetworkCounts,
    rememberUserEventsList,
    rememberAdminEventsList,
    fetchGuestsListCSV,
    fetchGuests,
  }), [
    state,
    networkCounts,
    getAdminScopeEvent,
    getUserScopeEvent,
    invalidateAdminEventsListFetch,
    invalidateUserEventsListFetch,
    fetchAdminEvents,
    createAdminEventsListFetcher,
    fetchMyEvents,
    fetchEvent,
    fetchMyEvent,
    createEvent,
    updateEvent,
    attendEvent,
    absentEvent,
    cancelEvent,
    deleteEvent,
    deleteEventType,
    duplicateEvent,
    clearNetworkCounts,
    rememberUserEventsList,
    rememberAdminEventsList,
    fetchGuestsListCSV,
    fetchGuests,
  ]);

  return (
    <EventsContext.Provider value={value}>
      {children}
    </EventsContext.Provider>
  );
}

export const EventsProvider = memo(EventsProviderComponent);

export function useEvents() {
  const value = useContext(EventsContext);
  if (!value) throw new Error('useEvents must be used within EventsProvider');
  return value;
}
